/**
 * The frame every report prints inside.
 *
 * On screen it is nearly invisible -- a title and the toolbar. On paper it
 * becomes a letterhead: company identity, VAT and CR numbers, the period
 * covered, both calendars, page numbers, and signature blocks.
 *
 * A statement handed to a bank or an auditor is judged on this frame
 * before anyone reads a figure.
 */
import { type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Box, Paper, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { fetchCompany } from '../api/endpoints'
import { useBi } from '../components/Bi'
import { useAuth } from '../store/AuthContext'
import { c } from '../theme'
import { dualDate, gregorian, toHijri } from './hijri'

interface Props {
  titleKey: string
  /** Shown under the title: the period or as-at date. */
  period?: string
  toolbar?: ReactNode
  headRight?: ReactNode
  /** Signature blocks on paper. Off for working lists like the day book. */
  signatures?: boolean
  children: ReactNode
}

export default function ReportShell({
  titleKey, period, toolbar, headRight, signatures = true, children,
}: Props) {
  const { t, i18n } = useTranslation()
  const { me } = useAuth()
  const bi = useBi()
  const company = useQuery({ queryKey: ['company'], queryFn: fetchCompany })
  const co = company.data
  const lang = i18n.language
  const now = new Date()

  return (
    <Paper variant="outlined" className="report-sheet">
      {/* Letterhead -- print only */}
      <Box className="print-only report-letterhead">
        <Stack direction="row" spacing={2} alignItems="flex-start">
          {co?.logo && (
            <Box component="img" src={co.logo} alt=""
              sx={{ height: 56, width: 'auto', objectFit: 'contain' }} />
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 16, fontWeight: 700, lineHeight: 1.25 }}>
              {co?.name_en}
            </Typography>
            {co?.name_ar && (
              <Typography sx={{ fontSize: 14, fontWeight: 600, lineHeight: 1.3,
                fontFamily: "'IBM Plex Sans Arabic', sans-serif" }}>
                {co.name_ar}
              </Typography>
            )}
            {(co?.address_en || co?.address_ar) && (
              <Typography sx={{ fontSize: 10.5, color: c.inkMuted, mt: 0.3 }}>
                {lang === 'ar' ? (co?.address_ar || co?.address_en)
                               : (co?.address_en || co?.address_ar)}
              </Typography>
            )}
            <Typography sx={{ fontSize: 10.5, color: c.inkMuted }}>
              {[co?.phone, co?.email].filter(Boolean).join('  ·  ')}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'end', flexShrink: 0 }}>
            {co?.vat_number && (
              <Typography sx={{ fontSize: 10.5 }}>
                {t('report.vatNo')}{' '}
                <span className="num">{co.vat_number}</span>
              </Typography>
            )}
            {co?.cr_number && (
              <Typography sx={{ fontSize: 10.5 }}>
                {t('report.crNo')}{' '}
                <span className="num">{co.cr_number}</span>
              </Typography>
            )}
          </Box>
        </Stack>

        <Box className="rule-gold" sx={{ mt: 1, mb: 1.25 }} />

        <Stack direction="row" justifyContent="space-between" alignItems="flex-end">
          <Box>
            <Typography sx={{ fontSize: 14, fontWeight: 700,
              letterSpacing: '0.04em', textTransform: 'uppercase' }}>
              {t(titleKey)}
            </Typography>
            {period && (
              <Typography sx={{ fontSize: 10.5, color: c.inkMuted }}>{period}</Typography>
            )}
          </Box>
          <Box sx={{ textAlign: 'end' }}>
            <Typography sx={{ fontSize: 9.5, color: c.inkMuted }}>
              {t('report.generated')} {dualDate(now, lang)}
            </Typography>
            <Typography sx={{ fontSize: 9.5, color: c.inkMuted }}>
              {t('report.by')} {me?.full_name || me?.username}
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* Screen header */}
      <Box className="no-print">
        <Stack direction="row" alignItems="flex-start" spacing={2}
          sx={{ px: 2.5, pt: 2.25, pb: 0.5 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography component="h1" sx={{ m: 0, fontSize: 22, fontWeight: 600,
                                              letterSpacing: '-0.01em', lineHeight: 1.3 }}>
              {bi(titleKey)}
            </Typography>
            {period && (
              <Typography sx={{ fontSize: 13, color: c.inkMuted, mt: 0.25 }}>
                {period}
              </Typography>
            )}
          </Box>
          {headRight}
        </Stack>
        {toolbar}
      </Box>

      {children}

      {/* Signatures -- print only */}
      {signatures && (
        <Box className="print-only report-signatures">
          <Stack direction="row" spacing={4} sx={{ mt: 5 }}>
            {['preparedBy', 'checkedBy', 'approvedBy'].map((k) => (
              <Box key={k} sx={{ flex: 1 }}>
                <Box sx={{ borderTop: `1px solid ${c.ink}`, pt: 0.5 }}>
                  <Typography sx={{ fontSize: 10, color: c.inkMuted }}>
                    {t(`report.${k}`)}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        </Box>
      )}
    </Paper>
  )
}

/** Period label helpers, so every report words it identically. */
export function rangeLabel(from: string, to: string, lang: string,
                           t: (k: string) => string): string {
  // Both calendars: a statement handed to a Saudi business is expected to
  // carry the Hijri dates too.
  const h1 = toHijri(from, lang).replace(/\s*AH$/, '')
  const h2 = toHijri(to, lang)
  const hijri = h1 && h2 ? `  ·  ${h1} — ${h2}` : ''
  return `${t('report.forPeriod')} ${gregorian(from, lang)} — ${gregorian(to, lang)}${hijri}`
}

export function asAtLabel(date: string, lang: string,
                          t: (k: string) => string): string {
  return `${t('report.asAt')} ${dualDate(date, lang)}`
}
